/**
 * Prop contract between the quick-entry container (`QuickEntryV2.tsx`, and the unrendered
 * `QuickEntry.tsx`) and its desktop and mobile views (`V2/QuickEntryDesktopV2.tsx`,
 * `V2/QuickEntryMobileV2.tsx`, `QuickEntryDesktop.tsx`, `QuickEntryMobile.tsx`). The container
 * owns all state and API calls; the views only render it and call back.
 */
import React from "react";

import { Product, Subscribable } from "@ryerson/frontend.common";

/** A UOM dropdown option (`@ryerson/common.uom` values such as `lbs`, `pcs`). */
export interface DropdownOption {
	display: string;
	value: string;
}

/**
 * Third copy of the quick-entry target enum, not imported by any file. Callers use
 * `QuickEntry.tsx` → `QuickEntryDataType`. To be deleted, together with `QuickEntry.tsx`.
 */
export enum QuickEntryDataType {
	ShoppingCart = 1,
	ShoppingList = 2,
	QuoteList = 3,
}

/** Everything a quick-entry view needs from the container. */
export type QuickEntryProps = {
	/** Search field and results list; a click outside it closes the results. */
	searchInputContainerRef: React.RefObject<HTMLElement>;
	showDropdown: boolean;
	setShowDropdown: React.Dispatch<React.SetStateAction<boolean>>;
	/** Products returned by the Items search for the current term. */
	searchResult: Array<Product>;
	/** Selects a product: sets its UOM options and shows its `fastPath` in the input. */
	onClickSelectItem: (value: any) => void;
	/** The search returned nothing or failed; the views show "No Results Found". */
	isError: boolean;
	quantity: number | undefined;
	setQuantity: React.Dispatch<React.SetStateAction<number | undefined>>;
	selectedProduct: Product | undefined;
	/** Selected quantity UOM value. */
	qtyUOM: string;
	qtyUOMDropdown: DropdownOption[];
	/** Selected pricing UOM value. */
	priceUOM: string;
	priceUOMDropdown: DropdownOption[];
	/** Adds the selected product; `callBack` runs after a completed add. */
	onClickAddBelow: (callBack?: Function) => void;
	/** Text shown in the search input; differs from `searchTerm` once a product is picked. */
	displaySearchTerm: string | number;
	onChangeText: (value: any) => void;
	/** Text the search runs on. */
	searchTerm: string | number;
	setPriceUOM: (value: any) => void;
	showQuickEntry?: boolean;
	closeQuickEntry: () => void;
	/**
	 * Drive the "added below" / "not added below" popper on the Add Below button; reset after 5 s.
	 */
	itemAddSuccess: boolean;
	itemAddFail: boolean;
	updateQuantityUom: (value: string) => void;
	/** True after the container's first client render; the views focus the search input then. */
	hasMounted?: boolean;
	/**
	 * Set when Add Below is clicked, cleared when the search resets. Passed to the quantity input's
	 * `isSubmitted` so it validates; `V2/QuickEntryDesktopV2.tsx` passes `true` instead.
	 */
	isQuickEntrySubmitted: boolean;
	setIsQuickEntrySubmitted: React.Dispatch<React.SetStateAction<boolean>>;
	/** Called by the quantity input with `hasError` true when the quantity is empty or zero. */
	handleValidationResponse: (isValid: boolean, id: string | number) => void;
	/**
	 * Pub-sub channel between the price-UOM dropdown and the quantity input (`to-debounce` /
	 * `is-debouncing` messages).
	 */
	stateMessenger: Subscribable<string>;
	/** V2 only: the quantity input's last validation result. */
	isQuickEntryValid?: boolean;
};

/** Keys the desktop views handle to move through and pick from the search results. */
export enum SelectKeys {
	ArrowDown = "ArrowDown",
	ArrowUp = "ArrowUp",
	Enter = "Enter",
}
